import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { t } from '@lingui/core/macro'
import { Plural, Trans } from '@lingui/react/macro'
import { LoadingButton } from '@mui/lab'
import { Tooltip, Typography } from '@mui/material'
import { useErrorCorrectionContext } from 'error-correction'
import { WandSparkles } from 'lucide-react'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

const PURPLE_BUTTON_STYLE = {
  backgroundColor: '#8052FF',
  '&:hover': {
    backgroundColor: '#6D28D9',
  },
}

interface ErrorCorrectionButtonProps {
  onCorrectErrors: () => Promise<void>
  disabled: boolean
  selectedRowCount: number
}

const ErrorCorrectionButtonText = ({ rowCount }: { rowCount: number }) => (
  <Plural
    value={rowCount}
    _0="Smart Correct"
    one={
      <Trans>
        Smart Correct <span className="font-bold">1</span> row
      </Trans>
    }
    other={
      <Trans>
        Smart Correct <span className="font-bold">#</span> rows
      </Trans>
    }
    comment="Button to correct errors in a dataset"
  />
)

const genAiTooltip = (shouldShow = true) =>
  shouldShow ? (
    <>
      <Typography variant="h4">
        <Trans>Errors slowing you down? Try Smart Correct!</Trans>
      </Typography>
      <br />
      <Typography variant="body1">
        <Trans>
          Clean up data faster, improve metadata quality, and speed up your launch with fewer manual
          fixes.
        </Trans>
      </Typography>
    </>
  ) : null

export const ErrorCorrectionButton = ({
  onCorrectErrors,
  disabled,
  selectedRowCount,
}: ErrorCorrectionButtonProps) => {
  const [isGenAiFeatureEnabled, isErrorCorrectionFeatureEnabled] = useSelector(
    selectFeatureIncludes([FeatureDict.GEN_AI, FeatureDict.ERROR_CORRECTION]),
  )
  const { isLoading } = useErrorCorrectionContext()
  const handleClick = useCallback(() => {
    void onCorrectErrors()
  }, [onCorrectErrors])
  return (
    <Tooltip
      arrow
      variant={disabled ? 'white' : 'genAiTooltip'}
      title={
        disabled
          ? t`Select rows to enable Smart Correct`
          : genAiTooltip(isErrorCorrectionFeatureEnabled && !isGenAiFeatureEnabled)
      }
    >
      <span>
        <LoadingButton
          variant="contained"
          startIcon={
            <WandSparkles className={`text-white-${disabled ? '400' : '800'}`} size={18} />
          }
          onClick={handleClick}
          disabled={disabled}
          loading={isLoading}
          sx={PURPLE_BUTTON_STYLE}
        >
          <div>
            <ErrorCorrectionButtonText rowCount={selectedRowCount} />
          </div>
        </LoadingButton>
      </span>
    </Tooltip>
  )
}
